import { useCallback, useEffect, useState } from 'react';
import { api } from '../../../../lib/api.js';

// Whole-brief pulse state — singleton per (editor, isoDate). Optimistic local
// updates; server-side upsert keyed on (editorId, isoDate).
export function useBriefPulse(isoDate) {
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!isoDate) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`/brief-pulse/${isoDate}`);
      setPulse(data.pulse || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isoDate]);

  useEffect(() => {
    load();
  }, [load]);

  const update = useCallback(
    async (patch) => {
      setPulse((prev) => ({ ...(prev || { isoDate }), ...patch }));
      try {
        const data = await api.post('/brief-pulse', { isoDate, ...patch });
        setPulse(data.pulse);
      } catch (err) {
        setError(err.message);
      }
    },
    [isoDate]
  );

  const isComplete = !!(
    pulse &&
    pulse.initialScore &&
    pulse.nowScore &&
    pulse.effort
  );

  // Used by FinalCheck (PUT /brief-pulse/:isoDate writes reflection +
  // changeSummary out-of-band; FinalCheck owns the network call and hands us
  // the resulting doc to keep useBriefPulse's view in sync).
  const replace = useCallback((next) => {
    if (next) setPulse(next);
  }, []);

  return { pulse, loading, error, update, replace, isComplete, reload: load };
}
